import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { publicAPI } from '../lib/publicAPI';
import { RepeatableFieldArray } from '../components/forms/RepeatableFieldArray';
import { Building2, CheckCircle } from 'lucide-react';

const rentalFields = [
  { name: 'property_address', label: 'Property Address', required: true, placeholder: '123 Main St, Sydney NSW 2000' },
  { name: 'property_type', label: 'Property Type', placeholder: 'residential / commercial' },
  { name: 'ownership_structure', label: 'Ownership', placeholder: 'individual / company / trust / smsf' },
  { name: 'ownership_percentage', label: 'Ownership %', type: 'number', placeholder: '100' },
  { name: 'purchase_date', label: 'Purchase Date', type: 'date' },
  { name: 'purchase_price', label: 'Purchase Price', type: 'number', placeholder: '750000' },
];

const registrationSchema = z.object({
  business_name: z.string().min(2, 'Business name required'),
  entity_type: z.enum(['company', 'trust', 'smsf', 'sole_trader', 'individual']),
  abn: z.string().regex(/^\d{11}$/, 'ABN must be 11 digits').optional().or(z.literal('')),
  acn: z.string().regex(/^\d{9}$/, 'ACN must be 9 digits').optional().or(z.literal('')),
  gst_registered: z.boolean(),
  bas_cycle: z.string().optional(),
  contact_name: z.string().min(2, 'Contact name required'),
  contact_role: z.string().optional(),
  contact_email: z.string().email('Valid email required'),
  contact_phone: z.string().min(10, 'Valid phone required'),
  message: z.string().optional(),
});

export default function PublicClientRegistration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [firm, setFirm] = useState(null);
  const [firmError, setFirmError] = useState('');
  useEffect(() => {
    const slug = searchParams.get('firm');
    if (!slug) { setFirmError('Please use the registration link your accountant sent you.'); return; }
    publicAPI.firm(slug).then((r) => setFirm(r.data)).catch((e) => setFirmError(e.response?.data?.detail || 'Practice not found'));
  }, [searchParams]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [hasRentals, setHasRentals] = useState(false);
  const [rentalProperties, setRentalProperties] = useState([]);
  
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      entity_type: 'company',
      gst_registered: false,
      bas_cycle: 'quarterly'
    }
  });

  const gstRegistered = watch('gst_registered');

  const services = [
    { id: 'bas', label: 'BAS Preparation & Lodgment' },
    { id: 'tax_return', label: 'Tax Return Preparation' },
    { id: 'payroll', label: 'Payroll Services' },
    { id: 'bookkeeping', label: 'Bookkeeping' },
    { id: 'advisory', label: 'Tax Advisory' },
  ];

  const toggleService = (serviceId) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]
    );
  };

  // When they tick "yes", show one empty property row straight away so the form is obvious.
  const toggleHasRentals = (checked) => {
    setHasRentals(!!checked);
    if (checked && rentalProperties.length === 0) {
      setRentalProperties([Object.fromEntries(rentalFields.map((f) => [f.name, '']))]);
    }
  };

  const onSubmit = async (data) => {
    if (ENTITY_ROLES[data.entity_type] && !ENTITY_ROLES[data.entity_type].some(([k]) => k === data.contact_role)) {
      toast.error('Please select your role in the entity');
      return;
    }
    setLoading(true);
    try {
      await publicAPI.registerClient({
        ...data,
        contact_role: ENTITY_ROLES[data.entity_type] ? data.contact_role : null,
        services_needed: selectedServices,
        has_rentals: hasRentals,
        rental_properties: hasRentals
          ? rentalProperties.filter((p) => (p.property_address || '').trim())
          : [],
        firm_slug: firm?.slug
      });
      
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (firmError) {
    return <div className="min-h-screen flex items-center justify-center p-6"><p className="text-sm" data-testid="register-client-firm-error">{firmError}</p></div>;
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted px-4">
        <Card className="w-full max-w-2xl shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
              Registration Submitted!
            </CardTitle>
            <CardDescription className="text-base mt-4" data-testid="register-client-success-text">
              Thanks — we'll review your details and email you an invitation to set up your secure portal login.
            </CardDescription>
          </CardHeader>
          {firm?.website && (
            <CardContent className="flex justify-center pt-2">
              <a href={firm.website} rel="noopener noreferrer" data-testid="register-client-back-to-website">
                <Button variant="outline">Back to {firm.name} website</Button>
              </a>
            </CardContent>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center">
              <Building2 className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
            Client Registration
          </h1>
          <p className="text-muted-foreground">Join {firm?.name || 'your accountant'} - Complete the form below to get started</p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Business Details</CardTitle>
            <CardDescription>Tell us about your business</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Business Name */}
              <div className="space-y-2">
                <Label htmlFor="business_name">Business Name *</Label>
                <Input
                  id="business_name"
                  placeholder="e.g., ABC Pty Ltd"
                  {...register('business_name')}
                  data-testid="reg-business-name"
                />
                {errors.business_name && (
                  <p className="text-sm text-red-500">{errors.business_name.message}</p>
                )}
              </div>

              {/* Entity Type */}
              <div className="space-y-3">
                <Label>Entity Type *</Label>
                <RadioGroup
                  value={watch('entity_type')}
                  onValueChange={(value) => setValue('entity_type', value)}
                  className="grid grid-cols-2 gap-4"
                >
                  {[
                    { value: 'company', label: 'Company' },
                    { value: 'trust', label: 'Trust' },
                    { value: 'smsf', label: 'SMSF' },
                    { value: 'sole_trader', label: 'Sole Trader' },
                    { value: 'individual', label: 'Individual' },
                  ].map((option) => (
                    <Label
                      key={option.value}
                      htmlFor={`entity-${option.value}`}
                      className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-muted/50"
                    >
                      <RadioGroupItem value={option.value} id={`entity-${option.value}`} />
                      <span>{option.label}</span>
                    </Label>
                  ))}
                </RadioGroup>
              </div>

              {/* ABN & ACN */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="abn">ABN</Label>
                  <Input
                    id="abn"
                    placeholder="12345678901"
                    {...register('abn')}
                    maxLength={11}
                    className="font-mono"
                  />
                  {errors.abn && <p className="text-sm text-red-500">{errors.abn.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="acn">ACN</Label>
                  <Input
                    id="acn"
                    placeholder="123456789"
                    {...register('acn')}
                    maxLength={9}
                    className="font-mono"
                  />
                  {errors.acn && <p className="text-sm text-red-500">{errors.acn.message}</p>}
                </div>
              </div>

              {/* GST & BAS */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="gst_registered"
                    checked={watch('gst_registered')}
                    onCheckedChange={(checked) => setValue('gst_registered', checked)}
                  />
                  <Label htmlFor="gst_registered" className="cursor-pointer">GST Registered</Label>
                </div>

                {gstRegistered && (
                  <div className="space-y-2">
                    <Label htmlFor="bas_cycle">BAS Cycle</Label>
                    <Select
                      value={watch('bas_cycle')}
                      onValueChange={(value) => setValue('bas_cycle', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="annually">Annually</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <hr className="my-6" />

              {/* Contact Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">Primary Contact</h3>
                {ENTITY_ROLES[watch('entity_type')] && (
                  <div className="space-y-2">
                    <Label>Your role *</Label>
                    <Select value={watch('contact_role') || ''} onValueChange={(v) => setValue('contact_role', v)}>
                      <SelectTrigger data-testid="reg-contact-role"><SelectValue placeholder="Select your role" /></SelectTrigger>
                      <SelectContent>
                        {ENTITY_ROLES[watch('entity_type')].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">You'll be recorded as the authorised signatory for this entity.</p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Full Name *</Label>
                  <Input
                    id="contact_name"
                    placeholder="John Smith"
                    {...register('contact_name')}
                  />
                  {errors.contact_name && <p className="text-sm text-red-500">{errors.contact_name.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_email">Email *</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    placeholder="john@example.com"
                    {...register('contact_email')}
                  />
                  {errors.contact_email && <p className="text-sm text-red-500">{errors.contact_email.message}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Phone *</Label>
                  <Input
                    id="contact_phone"
                    placeholder="0400 000 000"
                    {...register('contact_phone')}
                  />
                  {errors.contact_phone && <p className="text-sm text-red-500">{errors.contact_phone.message}</p>}
                </div>
              </div>

              <hr className="my-6" />

              {/* Services Needed */}
              <div className="space-y-3">
                <Label>Services Needed (optional)</Label>
                <div className="grid grid-cols-2 gap-3">
                  {services.map((service) => (
                    <Label
                      key={service.id}
                      htmlFor={`service-${service.id}`}
                      className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-muted/50"
                    >
                      <Checkbox
                        id={`service-${service.id}`}
                        checked={selectedServices.includes(service.id)}
                        onCheckedChange={() => toggleService(service.id)}
                      />
                      <span className="text-sm">{service.label}</span>
                    </Label>
                  ))}
                </div>
              </div>

              <hr className="my-6" />

              {/* Rental Properties */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2 p-3 border rounded-lg bg-muted/50">
                  <Checkbox
                    id="has_rentals"
                    checked={hasRentals}
                    onCheckedChange={toggleHasRentals}
                    data-testid="reg-has-rentals"
                  />
                  <Label htmlFor="has_rentals" className="cursor-pointer">Do you have rental / investment properties?</Label>
                </div>

                {hasRentals && (
                  <div className="space-y-2">
                    <RepeatableFieldArray
                      label="Rental Properties"
                      items={rentalProperties}
                      onItemsChange={setRentalProperties}
                      fieldConfig={rentalFields}
                      addLabel="Add Another Property"
                    />
                    <p className="text-xs text-muted-foreground">
                      Add each property you own. These are sent to your accountant and appear on
                      their Rental Properties dashboard.
                    </p>
                  </div>
                )}
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message">Additional Information (optional)</Label>
                <Textarea
                  id="message"
                  placeholder="Tell us about your accounting needs..."
                  {...register('message')}
                  rows={3}
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="submit-registration"
              >
                {loading ? 'Submitting...' : 'Submit Registration'}
              </Button>

              <p className="text-xs text-center text-muted-foreground mt-4">
                Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in here</Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
